import { useRef, useEffect, useState, useCallback } from "react";
import Map, { NavigationControl, GeolocateControl, Marker } from "react-map-gl";
import * as photosData from "./data/photosData.json";
import * as tileData from "./data/tileData.json";
import * as journalData from "./data/journalData.json";
import * as videoData from "./data/videoData.json";
import * as suggestionsData from "./data/suggestionsData.json";
import * as turf from "@turf/turf";
import shuffle from "./utils/shuffleArray";
import distance from "@turf/distance";
import ThumbPanel from "./components/thumbPanel.tsx";
import ClusterLayer from "./components/clusterLayers.jsx";
import RastersWidget from "./components/rasterWidget";
import SpotlightOverlay from "./components/spotlightOverlay";
import HomelessWidget from "./components/homelessWidget.jsx";
import LocationEditWidget from "./components/locationEditWidget.jsx";
import SuggestionsLayer from "./components/suggestionsLayer.jsx";
import Header from "./components/header";
import MapLanguageControl from "./components/languageControl";
import YearRangeSlider from "./components/yearRangeSlider";
import SuggestionInput from "./components/suggestionInput";
import ItemModal from "./components/itemModal";
import InfoPopup from "./components/infoPopup";
import MarkerLayer from "./components/markerLayer";
import SpiderLayer from "./components/spiderLayer.jsx";
import PositionPrompt from "./components/positionPrompt";
import DeletionPrompt from "./components/deletionPrompt";
import { signInWithPopup, auth, googleProvider } from "./firebase/init";
import { fetchItems } from "./controller";
import { searchItems as searchItemsApi, searchByGeotagger, fetchComments, addComment as addCommentApi, toggleQuestionable, getDistinctCreators, getYearBounds } from "./supabaseApi";
import { supabase } from "./supabase";
import API from "./api";
import UploadModal from "./components/uploadModal";
import InfoModal from "./components/infoModal";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import { IconButton, Tooltip } from "@mui/material";
import SearchWidget from "./components/searchWidget";
import BeamLayer from "./components/beamLayer.jsx";
import getAngleFromBearing from "./utils/getAngleFromBearing";
import ExternalWidget from "./components/externalWidget.jsx";
import ThumbMarkerLayer from "./components/thumbMarker.jsx";
import PointLayer from "./components/pointLayer.jsx";
import StreetViewPanel from "./components/streetViewPanel.tsx";
import arrowIcon1 from './assets/arrow-up-type-1.png';
import arrowIcon2 from './assets/arrow-up-type-2.png';
import arrowIcon3 from './assets/arrow-up-type-3.png';
import arrowIcon1q from './assets/arrow-up-type-1-q.png';
import arrowIcon2q from './assets/arrow-up-type-2-q.png';
import arrowIcon3q from './assets/arrow-up-type-3-q.png';
import { colors } from './utils/colorDict';
import { loadRemoteStrings } from './utils/strings';
import { trackEvent } from './utils/analytics';

export default function App() {
	const mapRef = useRef(null);
	const savedView = JSON.parse(localStorage.getItem('mapViewState') || 'null');
	const [lng, setLng] = useState(savedView?.longitude || 34.76527378385479);
	const [lat, setLat] = useState(savedView?.latitude || 32.06016139222424);
	const [zoom, setZoom] = useState(null);
	const [bounds, setBounds] = useState(null);
	const [photoModalData, setPhotoModalData] = useState(null);
	const [journalModalData, setJournalModalData] = useState(null);
	const [videoModalData, setVideoModalData] = useState(null);
	const [spiderData, setSpiderData] = useState([]);
	const [suggestionList, setSuggestionList] = useState([]);
	const [selectedMedia, setSelectedMedia] = useState([1, 2, 3]);
	const [infoPopupData, setInfoPopupData] = useState(null);
	const [rastersWidgetOpen, setRastersWidgetOpen] = useState(false);
	const [rastersHidden, setRastersHidden] = useState(false);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [photoMarkerListInBounds, setPhotoMarkerListInBounds] = useState([]);
	const [journalMarkerListInBounds, setJournalMarkerListInBounds] = useState([]);
	const [itemsToMark, setItemsToMark] = useState([]);
	const [rightPanel, setRightPanel] = useState(0);
	const [filterString, setFilterString] = useState("");
	const [itemToPosition, setItemToPosition] = useState(null);
	const [lastGeotaggedId, setLastGeotaggedId] = useState(null);
	const [lastRemovedLocationId, setLastRemovedLocationId] = useState(null);
	const geotagSourcePanel = useRef(null);
	const [itemToSuggest, setItemToSuggest] = useState(null);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [suggestionInputOpen, setSuggestionInputOpen] = useState(false);
	const [inputxy, setInputxy] = useState({ x: null, y: null });
	const [suggestionLngLat, setSuggestionLngLat] = useState(null);
	const [positionLngLat, setPositionLngLat] = useState(null);
	const [searchItems, setSearchItems] = useState([]);
	// Remember the last submitted search so we can auto-rerun it when filters
	// (currently: selectedCreators) change. null = no search currently active.
	const [lastSearch, setLastSearch] = useState(null); // { query, mode } | null
	const [mergedMedia, setMergedMedia] = useState([]);
	const [authData, setAuthData] = useState(null);
	const [itemsFromApi, setItemsFromApi] = useState([]);
	const [itemComments, setItemComments] = useState([]);
	const [itemPk, setItemPk] = useState(null);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const [positionPromptOpen, setPositionPromptOpen] = useState(false);
	const [deletionPromptOpen, setDeletionPromptOpen] = useState(false);
	const [suggestions, setSuggestions] = useState([]);
	const [suggesting, setSuggesting] = useState(false);
	const [itemYears, setItemYears] = useState([1900, 2022]);
	const [filteredItems, setFilteredItems] = useState([]);
	const [thumbPanelItems, setThumbPanelItems] = useState([]);
	const [fetchingProgress, setFetchingProgress] = useState(false);
	const [beamData, setBeamData] = useState(null);
	const [cursorLngLat, setCursorLngLat] = useState(null);
	const [cursorScreenPos, setCursorScreenPos] = useState(null);
	const [angle, setAngle] = useState(null);
	//const [sat, setSat] = useState(false);
	const [thumbDrawerOpen, setThumbDrawerOpen] = useState(false);
	const [markedThumbnail, setMarkedThumbnail] = useState(null);
	const [addressMarker, setAddressMarker] = useState(null);
	const [mapStyle, setMapStyle] = useState(process.env.REACT_APP_MAPBOX_STYLE);
	const [editMode, setEditMode] = useState(false);
	const [itemsToEdit, setItemsToEdit] = useState([]);
	const [showQuestionableOnly, setShowQuestionableOnly] = useState(false);
	// Unified-site filters (creator + year range). Empty string / null = no filter applied.
	const [selectedCreators, setSelectedCreators] = useState([]); // header filter — drives map dots only
	const [creatorOptions, setCreatorOptions] = useState([]);
	// Independent creator filters per widget. Each is seeded from `selectedCreators`
	// when its panel opens (rightPanel transitions into the panel's value), then
	// drifts independently. Header changes do NOT propagate while a panel is open.
	const [searchCreators, setSearchCreators] = useState([]);
	const [homelessCreators, setHomelessCreators] = useState([]);
	const [yearBounds, setYearBounds] = useState(null); // [min, max] | null
	const [yearRange, setYearRange] = useState(null);   // [lo, hi] | null = no filter
	// Phone vs desktop layout. Phone hides the thumb slider, switches the year slider to a
	// horizontal bottom bar, and replaces zoom buttons with a "my location" compass.
	const [isPhone, setIsPhone] = useState(() =>
		typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches);
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const mq = window.matchMedia('(max-width: 600px)');
		const onChange = () => setIsPhone(mq.matches);
		mq.addEventListener('change', onChange);
		return () => mq.removeEventListener('change', onChange);
	}, []);
	const [streetViewData, setStreetViewData] = useState(null);
	const [renderedRasters, setRenderedRasters] = useState([]);
	const [spotlightMode, setSpotlightMode] = useState(false);
	const [viewState, setViewState] = useState({ longitude: lng, latitude: lat, zoom: savedView?.zoom || 14 });
	const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');

	const toggleLang = () => {
		setLang(prev => {
			const next = prev === 'en' ? 'he' : 'en';
			localStorage.setItem('lang', next);
			return next;
		});
	};

	const toggleMapStyle = () => {
		if (mapStyle === process.env.REACT_APP_MAPBOX_STYLE) {
			setMapStyle('mapbox://styles/mapbox/satellite-v9');
		} else {
			setMapStyle(process.env.REACT_APP_MAPBOX_STYLE);
		}
	};

	const sortedRasterData = tileData.default.sort((a, b) => a.year - b.year);

	useEffect(() => {
		setRenderedRasters(sortedRasterData.map(raster => ({ ...raster, opacity: 0 })));
		loadRemoteStrings();
		trackEvent('page_view', { metadata: { referrer: document.referrer || null, lang: localStorage.getItem('lang') || 'en' } });
		// Unified site: fetch creator list and year bounds for the filters in the header / right edge.
		// Both calls are cheap and fire-and-forget; the per-collection sites use them too (single creator, narrow range).
		Promise.all([
			getDistinctCreators().catch(() => []),
			getYearBounds().catch(() => null),
		]).then(([creators, bounds]) => {
			if (Array.isArray(creators)) setCreatorOptions(creators);
			if (bounds && bounds.min_year != null && bounds.max_year != null) {
				setYearBounds([bounds.min_year, bounds.max_year]);
				// Leave yearRange = null until the user touches the slider — no filter applied initially.
			}
		});
	}, []);

	const hasActiveRaster = renderedRasters.some(r => r.opacity > 0);

	// Auto-close raster sidebar when spotlight activates
	useEffect(() => {
		if (spotlightMode) setRastersWidgetOpen(false);
	}, [spotlightMode]);

	const onStyleLoad = (event) => {
		const map = event.target; // Access the Mapbox GL JS Map instance
		const arrowIcons = [
			[arrowIcon1, 'arrowIcon1'], [arrowIcon2, 'arrowIcon2'], [arrowIcon3, 'arrowIcon3'],
			[arrowIcon1q, 'arrowIcon1q'], [arrowIcon2q, 'arrowIcon2q'], [arrowIcon3q, 'arrowIcon3q']
		];
		const addSourceAndLayer = () => {

			arrowIcons.forEach(([iconSrc, iconName]) => {
				const image = new Image(24, 24);
				image.src = iconSrc;

				image.onload = () => {
					if (!map.hasImage(iconName)) {
						// Recolor non-white pixels to collection color
						const canvas = document.createElement('canvas');
						canvas.width = 24; canvas.height = 24;
						const ctx = canvas.getContext('2d');
						ctx.drawImage(image, 0, 0, 24, 24);
						const imageData = ctx.getImageData(0, 0, 24, 24);
						const d = imageData.data;
						// Parse target color
						const tc = parseInt(colors[1].slice(1), 16);
						const tr = (tc >> 16) & 255, tg = (tc >> 8) & 255, tb = tc & 255;
						for (let i = 0; i < d.length; i += 4) {
							if (d[i+3] === 0) continue; // skip transparent
							// If pixel is not near-white (brightness < 200), recolor it
							const brightness = (d[i] + d[i+1] + d[i+2]) / 3;
							if (brightness < 200) {
								const ratio = brightness / 128; // preserve relative luminance
								d[i]   = Math.min(255, Math.round(tr * ratio));
								d[i+1] = Math.min(255, Math.round(tg * ratio));
								d[i+2] = Math.min(255, Math.round(tb * ratio));
							}
						}
						map.addImage(iconName, { width: 24, height: 24, data: new Uint8Array(d.buffer) });
					}
				}}
				);
		  if (!map.getSource('my-data')) {

			map.addSource('my-data', {
			  type: 'geojson',
			  data:  {
				type: 'FeatureCollection',
				features: filteredItems
			  }
			});

			map.addLayer({
			  id: 'pointLayer',
			  type: 'symbol',
			  source: 'my-data',
			  layout: {
				"icon-image": [
					'case',
					['>', ['length', ['get', 'questionable_by_str']], 2],
					['match',
						['get', 'mediatype'],
						'image', 'arrowIcon1q',
						'video', 'arrowIcon2q',
						'audio', 'arrowIcon3q',
						'arrowIcon1q'
					],
					['match',
						['get', 'mediatype'],
						'image', 'arrowIcon1',
						'video', 'arrowIcon2',
						'audio', 'arrowIcon3',
						'arrowIcon1'
					]
				],
				"icon-rotate": ["get", "angle"],
				// Angle is a compass bearing (north-relative). Align to map so arrows
				// keep pointing at the same real-world direction when the map rotates.
				"icon-rotation-alignment": "map",
				'icon-anchor': 'center',
				'icon-allow-overlap': true,
				"icon-size": [
					"interpolate",
					["linear"],
					["zoom"],
					10, 0.4,  // At zoom level 10, icon size is 0.4
					14, 0.8,  // At zoom level 14, icon size is 0.6
					18, 1.2   // At zoom level 18, icon size is 1.2
				]
			  }
			});
			//console.log(map)
		  }
		};
	  
		if (map.isStyleLoaded()) {
		  console.log('on style load triggered');
		  addSourceAndLayer();
		} else {
		  // Style is not yet loaded.
		  const intervalId = setInterval(() => {
			if (map.isStyleLoaded()) {
			  console.log('on style load finally triggered');
			  addSourceAndLayer();
			  clearInterval(intervalId); // Clear the interval once the source and layer are added
			}
		  }, 200); // Check every 200ms
		}
	  };

	// const handleSat = () => {
	// 	setSat((sat) => !sat);
	// };
	const cancelBeam = useCallback((_p1, _p2) => {
		setBeamData(null);
	}, []);

	// Handlers

	const handleSuggestions = useCallback((_data) => {
		setSuggestions(_data);
	},[]);

	const handleSearch = async (_str, mode = 'title') => {
		try {
			// The search panel runs against its OWN creator filter (`searchCreators`),
			// independent of the header creator filter once the panel is open. The
			// panel-open effect below seeds it from the header's current value.
			const creators = (Array.isArray(searchCreators) && searchCreators.length > 0) ? searchCreators : undefined;
			let results = mode === 'geotagger'
				? await searchByGeotagger(_str, creators)
				: await searchItemsApi(_str, creators);
			// thumbnail_url is now computed server-side by the edge function
			setSearchItems(results);
			setLastSearch({ query: _str, mode });
			setThumbDrawerOpen(false);
			setRightPanel(5);
			trackEvent('search', { metadata: { query: _str, mode, result_count: results?.length || 0, creators_filter: creators?.length || 0 } });
		} catch (err) {
			console.error('Search error:', err);
			alert('Search failed: ' + (err?.message || 'Unknown error'));
		}
	};

	// Auto-rerun the active search whenever the SEARCH widget's local creator
	// filter changes (changing the header filter no longer triggers a refresh).
	const searchCreatorsKey = (Array.isArray(searchCreators) ? searchCreators.slice().sort().join('|') : '');
	useEffect(() => {
		// Only refresh while the search results panel is actually open — otherwise
		// each dropdown change would silently re-hit the API.
		if (rightPanel !== 5) return;
		if (!lastSearch || !lastSearch.query) return;
		let cancelled = false;
		(async () => {
			try {
				const creators = (Array.isArray(searchCreators) && searchCreators.length > 0) ? searchCreators : undefined;
				const results = lastSearch.mode === 'geotagger'
					? await searchByGeotagger(lastSearch.query, creators)
					: await searchItemsApi(lastSearch.query, creators);
				if (!cancelled) setSearchItems(results);
			} catch (err) {
				console.error('Search refresh error:', err);
			}
		})();
		return () => { cancelled = true; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchCreatorsKey]);

	// Seed each widget's local creator filter from the header's current value
	// the FIRST time the user opens that widget in this session. Subsequent
	// closes + reopens preserve whatever the user set inside the widget — so
	// the panel reopens to where the user left it.
	const searchSeededRef = useRef(false);
	const homelessSeededRef = useRef(false);
	useEffect(() => {
		if (rightPanel === 5 && !searchSeededRef.current) {
			setSearchCreators([...(selectedCreators || [])]);
			searchSeededRef.current = true;
		}
		if (rightPanel === 1 && !homelessSeededRef.current) {
			setHomelessCreators([...(selectedCreators || [])]);
			homelessSeededRef.current = true;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rightPanel]);

	// const handleFav = async (_data) => {
	// 	let res = await API.post("items/updatefav/", _data)
	// 		.then((res) => res.data)
	// 		.catch((err) => console.log(err));
	// 	console.log(res);
	// 	let new_obj = res.features[0];
	// 	//setItemsFromApi(items=>items.map(item => res.features.find(_item => _item.id === item.id) || item))
	// 	setItemsFromApi((prevState) => {
	// 		const newState = prevState.map((obj) => {
	// 			//console.log(obj.properties.faved_by,new_obj.properties.faved_by)
	// 			if (obj.id === new_obj.id) {
	// 				return new_obj;
	// 			} else {
	// 				return obj;
	// 			}
	// 		});
	// 		return newState;
	// 	});
		
	// 	handleModalData(new_obj.properties, new_obj.properties.mediatype);
	// };

	const handleComment = async (_data) => {
		try {
			let res = await addCommentApi(_data.text, _data.item);
			setItemComments(res);
			trackEvent('comment_added', { item_id: _data.item });
		} catch (err) {
			console.log(err);
		}
	};

	const getAngle = (pos1, pos2) => {
		//console.log(pos1, pos2);
		var point1 = turf.point([pos1.lng, pos1.lat]);
		var point2 = turf.point([pos2[0], pos2[1]]);

		var bearing = turf.bearing(point1, point2);
		let _angle = getAngleFromBearing(bearing);
		return _angle;
	};

	const handleAuthData = (_data) => {
		setAuthData(_data);
	};

	const handleInfoPopupData = (_val) => {
		setInfoPopupData(_val);
	};
	const handleItemsToMark = useCallback((_items) => {
		setItemsToMark(_items);
	}, []);

	const handleMarkedThumbnail = useCallback((_item) => {
		setMarkedThumbnail(_item);
	}, []);

	const handleThumbDrawerOpen = useCallback(() => {
		setThumbDrawerOpen((open) => !open);
	}, []);

	// Close-only: both call sites in suggestionInput (submit and cancel) mean close.
	const handleSuggestionInputOpen = () => {
		setSuggestionInputOpen(false);
	};

	const handleItemToSuggest = useCallback((_item) => {
		setItemToSuggest(_item);
	}, []);

	const handleItemToDelete = useCallback((_item) => {
		setItemToDelete(_item);
	}, []);

	const handleItemsToeEdit = useCallback((_items) => {
		setItemsToEdit(_items);
	}, []);

	const handleItemToPosition = useCallback((_item, suggesting = false) => {
		setItemToPosition(_item);

		// Clear both prompts. suggestionInputOpen is reset nowhere else except the
		// ESC handler, so a suggestion abandoned by any other route (clicking away,
		// or starting a normal geotag) leaves it set — and because both prompts are
		// 200px white boxes anchored to the same inputxy, the next geotag shows an
		// empty one stacked against the approval frame until a re-render clears it.
		setPositionPromptOpen(false);
		setSuggestionInputOpen(false);

		if (suggesting) {
			setSuggesting(true);
		} else {
			setSuggesting(false);
		}
	}, [suggesting]);

	const handleSelectedMedia = (_val) => {
		setSelectedMedia(_val);
	};

	const handleFilterString = (_str) => {
		setFilterString(_str);
	};

	const handleRightPanel = useCallback((_val) => {
		if (_val === 0) {
			setEditMode(false);
		}
		if (_val !== 0) {
			setThumbDrawerOpen(false);
		}
		setRightPanel(_val);
	}, []);

	const handleRastersWidgetOpen = () => {
		setRastersWidgetOpen((open) => !open);
	};

	const resetRasters = () => {
		setRenderedRasters(rasters => rasters.map(r => ({ ...r, opacity: 0 })));
		setRastersWidgetOpen(false);
	};

	const handleUploadModal = () => {
		setUploadModalOpen((open) => !open);
	};
	const handleInfoModal = () => {
		setInfoModalOpen((open) => !open);
	};
	// Close-only: both call sites in positionPrompt (confirm and cancel) mean close.
	const handlePositionPromptOpen = () => {
		setPositionPromptOpen(false);
	};
	const handleDeletionPromptOpen = () => {
		setDeletionPromptOpen((open) => !open);
	};
	const handleItemYears = (_vals) => {
		setItemYears(_vals);
	};

	const signInWithGoogle = async () => {
		try {
			const res = await signInWithPopup(auth, googleProvider);
		} catch (err) {
			console.error(err);
		}
	};

	const handleModalData = useCallback((_item) => {
		let _mediatype = _item?.mediatype;
		if (_item?.id) {
			setItemPk(_item?.id);
			setItemComments([]);
			fetchComments(_item.id)
				.then(res => setItemComments(res))
				.catch(err => console.log(err));
			trackEvent('modal_open', { item_id: _item.id });
		}
		setPhotoModalData(_item, _mediatype);
		if (_item) {
			setModalIsOpen(true);
		} else {
			setModalIsOpen(false);
		}
	}, []);

	const handleSuggestionUpdate = (_note = "", _lngLat) => {
		setItemToSuggest((item) => ({
			...item,
			suggestions: [
				...item.suggestions,
				{
					note: _note,
					coordinates: {
						latitude: _lngLat.lat,
						longitude: _lngLat.lng,
					},
				},
			],
		}));
		setItemToPosition(null);
	};

	// Effects
	//console.log(filteredItems)
	useEffect(() => {
		//console.log('photos data event triggered')
		setPhotoMarkerListInBounds(
			photosData.default
				.filter(
					(item) =>
						Number(item.coordinates.latitude) < bounds?._ne?.lat &&
						Number(item.coordinates.latitude) > Number(bounds?._sw?.lat) &&
						Number(item.coordinates.longitude) < Number(bounds?._ne?.lng) &&
						Number(item.coordinates.longitude) > Number(bounds?._sw?.lng)
				)
				.filter(
					(item) =>
						!filterString.length ||
						item.title
							.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " ")
							.includes(
								filterString.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " ")
							)
				)
				.filter(
					(item) => item.year <= itemYears[1] && (item.year_end || item.year) >= itemYears[0]
				)
		);
	}, [bounds, filterString, itemYears]);

	// this event triggers all images download 

	useEffect(() => {
		if (!mapRef.current) return;
		mapRef.current.on("mousemove", (e) => {
			setCursorLngLat([e.lngLat.lng, e.lngLat.lat]);
			setCursorScreenPos({ x: e.point.x, y: e.point.y });
		});
	}, [mapRef.current]);

	useEffect(() => {
		const restoreAuth = async () => {
			let data = localStorage.getItem("authdata")
				? JSON.parse(localStorage.getItem("authdata"))
				: null;
			if (data) {
				// Refresh role from Supabase session in case it changed
				try {
					const { data: { user } } = await supabase.auth.getUser();
					if (user) {
						data.user.role = user.user_metadata?.role || 'viewer';
						localStorage.setItem('authdata', JSON.stringify(data));
					}
				} catch (e) { /* use cached role */ }
				setAuthData(data);
			}
		};
		restoreAuth();
	}, []);

	const isInBounds = (item, bounds) => {
		const itemLat = item.geometry.coordinates[1];
		const itemLng = item.geometry.coordinates[0];
	
		return (
			itemLat <= bounds._ne.lat &&
			itemLat >= bounds._sw.lat &&
			itemLng <= bounds._ne.lng &&
			itemLng >= bounds._sw.lng
		);
	};	

	useEffect(() => {
		if (!bounds || !filteredItems) return;

		// Use the post-filter state so the lower thumb slider honours every active
		// filter (media type, questionable-only, creator multi-select, year range).
		const ids = filteredItems.map(item => item.id);
		let panelItems = filteredItems.filter((item, index) =>
		  isInBounds(item, bounds) &&
		  ids.indexOf(item.id) === index
		);

		// Exclude items obscured by the slider when it's open.
		// Project each item to screen pixels so the test stays correct when the
		// map is rotated (a lat-based cutoff only works when north is up).
		if (thumbDrawerOpen && mapRef.current) {
			const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
			const canvas = map.getCanvas();
			const sliderHeight = 184; // close btn + thumbnails + pagination
			const cutoffY = canvas.clientHeight - sliderHeight;
			panelItems = panelItems.filter(item => {
				const c = item.geometry?.coordinates;
				if (!c || c.length < 2) return false;
				const pt = map.project([c[0], c[1]]);
				return pt && pt.y < cutoffY;
			});
		}

		setThumbPanelItems(panelItems);
	}, [bounds, filteredItems, thumbDrawerOpen]);


	function splitBounds(bounds, rows, cols) {
		const deltaLat = (bounds._ne.lat - bounds._sw.lat) / rows;
		const deltaLng = (bounds._ne.lng - bounds._sw.lng) / cols;
	
		let splittedBounds = [];
	
		for (let i = 0; i < rows; i++) {
			for (let j = 0; j < cols; j++) {
				splittedBounds.push({
					_ne: {
						lat: bounds._sw.lat + deltaLat * (i + 1),
						lng: bounds._sw.lng + deltaLng * (j + 1),
					},
					_sw: {
						lat: bounds._sw.lat + deltaLat * i,
						lng: bounds._sw.lng + deltaLng * j,
					},
				});
			}
		}
	
		return splittedBounds;
	}

	const computeDifferenceExtents = (oldBounds, newBounds) => {
		// Assuming bounds are objects with {_ne: {lat, lng}, _sw: {lat, lng}}
	
		let diffExtents = [];
	
		// Check if there's a new region above the oldBounds
		if (newBounds._ne.lat > oldBounds._ne.lat) {
			diffExtents.push({
				_ne: { lat: newBounds._ne.lat, lng: newBounds._ne.lng },
				_sw: { lat: oldBounds._ne.lat, lng: newBounds._sw.lng }
			});
		}
	
		// Check if there's a new region below the oldBounds
		if (newBounds._sw.lat < oldBounds._sw.lat) {
			diffExtents.push({
				_ne: { lat: oldBounds._sw.lat, lng: newBounds._ne.lng },
				_sw: { lat: newBounds._sw.lat, lng: newBounds._sw.lng }
			});
		}
	
		// Check if there's a new region to the right of the oldBounds
		if (newBounds._ne.lng > oldBounds._ne.lng) {
			diffExtents.push({
				_ne: { lat: oldBounds._ne.lat, lng: newBounds._ne.lng },
				_sw: { lat: oldBounds._sw.lat, lng: oldBounds._ne.lng }
			});
		}
	
		// Check if there's a new region to the left of the oldBounds
		if (newBounds._sw.lng < oldBounds._sw.lng) {
			diffExtents.push({
				_ne: { lat: oldBounds._ne.lat, lng: oldBounds._sw.lng },
				_sw: { lat: oldBounds._sw.lat, lng: newBounds._sw.lng }
			});
		}
	
		return diffExtents;
	}

	const lastFetchedBoundsRef = useRef(null);

	const fetchData = (extent, force = false) => {
		setFetchingProgress(true);

		fetchItems(
			`${extent._ne.lng},${extent._ne.lat}`,
			`${extent._sw.lng},${extent._sw.lat}`,
			force
		)
		.then(fetchedItems => {
			if (fetchedItems && fetchedItems.length > 0) {
			// thumbnail_url, right_panel_url, modal_url are now computed server-side
			// Add questionable_by_str for Mapbox expression (can't use arrays directly)
			fetchedItems.forEach(item => {
				if (item.properties) {
					const qb = item.properties.questionable_by;
					item.properties.questionable_by_str = Array.isArray(qb) ? JSON.stringify(qb) : (qb || '[]');
				}
			});

			setItemsFromApi(previousItems => {
				const newIds = new Set(fetchedItems.map(item => item.id));
				let filteredItems = previousItems.filter(item => !newIds.has(item.id));
				return [...filteredItems, ...fetchedItems];
			  });

			}
		})
		.catch(error => {
			console.error("Error fetching items:", error);
		})
		.finally(() => {
			setFetchingProgress(false);
				// Remove deleted item from items to map
				if (itemToDelete) {
				setItemsFromApi(previousItems => {
					const filteredItems = previousItems.filter(item => item.id !== itemToDelete.id);
					  
					return [...filteredItems];
				  });
			}});
	}
	//console.log(filteredItems)
	useEffect(() => {
		//if (!bounds || itemToPosition || !authData) return;
		if (!bounds || itemToPosition) return;
	
		let newExtents = [];
		if (lastFetchedBoundsRef.current) {
			newExtents = computeDifferenceExtents(lastFetchedBoundsRef.current, bounds);
		} else {
			newExtents.push(bounds);
		}
	
		newExtents.forEach(extent => {
			fetchData(extent);
		});
	
		lastFetchedBoundsRef.current = bounds;
	
	}, [bounds, itemToPosition, authData]);


	useEffect(() => {
		if (!itemsFromApi) return;

		let itemsFromApiFiltered = []

		let filtered = itemsFromApi.filter(
			(item) => selectedMedia.includes(item.properties.mediatype)
		);

		if (showQuestionableOnly) {
			filtered = filtered.filter(item => {
				const qb = item.properties.questionable_by;
				return Array.isArray(qb) ? qb.length > 0 : (qb && qb !== '[]');
			});
		}

		// Creator filter (unified site only — no-op when no creators selected = show all)
		if (selectedCreators && selectedCreators.length > 0) {
			const set = new Set(selectedCreators);
			filtered = filtered.filter(item => set.has(item.properties.creator));
		}

		// Year-range filter (active only when user has narrowed the slider; yearRange === null means inactive)
		if (yearRange && yearBounds && (yearRange[0] !== yearBounds[0] || yearRange[1] !== yearBounds[1])) {
			const [lo, hi] = yearRange;
			filtered = filtered.filter(item => {
				const y = item.properties.year;
				if (y == null) return false; // hide undated items when the year filter is active
				const yEnd = item.properties.year_end ?? y;
				return y <= hi && yEnd >= lo;
			});
		}

		setFilteredItems([...itemsFromApiFiltered, ...filtered]);

	}, [itemYears, itemsFromApi, selectedMedia, showQuestionableOnly, selectedCreators, yearRange, yearBounds]);

	// useEffect(() => {
	// 	if (!itemsFromApi) return
	// 	console.log('media type filter triggered')
	// 	console.log(itemsFromApi)
	// 	setFilteredItems(
	// 		itemsFromApi
	// 		// .filter(
	// 		// 	(item) =>
	// 		// 		item.properties.year >= itemYears[0] &&
	// 		// 		item.properties.year <= itemYears[1]
	// 		// )
	// 		.filter(
	// 			(item) =>
	// 				selectedMedia.includes(item.properties.mediatype)	
	// 		)
	// 	)
	// }, [itemYears, itemsFromApi, selectedMedia]);

	const journalYears = journalMarkerListInBounds
		.map((item) =>
			Math.round(100 - (100 / (2022 - 1900)) * (2022 - Number(item.year)))
		)
		.filter((item) => item >= 0)
		.slice(0, 50);

	let mergedMediaData = [];

	useEffect(() => {
		mergedMediaData = [];
		if (selectedMedia.includes(1)) {
			mergedMediaData = [
				...mergedMediaData,
				...photosData.default.map((item) => ({ ...item, mediatype: 1 })),
			];
		}
		if (selectedMedia.includes(2)) {
			mergedMediaData = [
				...mergedMediaData,
				...journalData.default.map((item) => ({ ...item, mediatype: 2 })),
			];
		}
		if (selectedMedia.includes(3)) {
			mergedMediaData = [
				...mergedMediaData,
				...videoData.default.map((item) => ({ ...item, mediatype: 3 })),
			];
		}
		mergedMediaData = mergedMediaData
			.filter((item) => item.year < itemYears[1] && (item.year_end || item.year) > itemYears[0])
			.filter(
				(item) =>
					Number(item.coordinates.latitude) < bounds?._ne?.lat &&
					Number(item.coordinates.latitude) > Number(bounds?._sw?.lat) &&
					Number(item.coordinates.longitude) < Number(bounds?._ne?.lng) &&
					Number(item.coordinates.longitude) > Number(bounds?._sw?.lng)
			)
			.map((item) => ({
				...item,
				distance: distance(
					turf.point([item.coordinates.latitude, item.coordinates.longitude]),
					turf.point([lat, lng])
				),
			}))
			.sort((a, b) =>
				a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0
			)
			.filter(
				(item) =>
					!filterString.length ||
					item.title
						.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " ")
						.includes(filterString.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " "))
			);
		setMergedMedia(shuffle(mergedMediaData));
	}, [selectedMedia, bounds, itemYears, filterString]);


	// Modal arrow-key navigation: cycle through map items sorted by distance from clicked item
	const modalNavList = useRef([]); // sorted items for arrow navigation
	const modalNavIndex = useRef(-1);
	// When true, stepModalNav eases the map to follow the highlighted item.
	// Set by search/homeless openers (lists span the whole city); cleared by
	// map-click opener (proximity list, items already on screen).
	const modalNavPan = useRef(false);

	// Build navigation list when modal opens from a map click.
	// Source the list from `filteredItems` (not raw `itemsFromApi`) so arrow
	// navigation only cycles through items actually shown on the map — i.e. it
	// honours the active collection / creator / year / questionable filters.
	const openModalWithNav = useCallback((clickedItem) => {
		const clickCoords = clickedItem.geometry?.coordinates;
		if (!clickCoords) {
			handleModalData(clickedItem);
			return;
		}
		// Sort all visible (filtered) items by distance from clicked item
		const visibleItems = filteredItems.filter(i =>
			i.geometry?.coordinates && isInBounds(i, bounds)
		);
		visibleItems.sort((a, b) => {
			const ac = a.geometry.coordinates;
			const bc = b.geometry.coordinates;
			const da = (ac[0] - clickCoords[0]) ** 2 + (ac[1] - clickCoords[1]) ** 2;
			const db = (bc[0] - clickCoords[0]) ** 2 + (bc[1] - clickCoords[1]) ** 2;
			return da - db;
		});
		modalNavList.current = visibleItems;
		modalNavIndex.current = 0; // clicked item is always first (distance 0)
		modalNavPan.current = false;
		setMarkedThumbnail(clickedItem);
		handleModalData(clickedItem);
	}, [filteredItems, bounds, handleModalData]);

	// Open modal with a pre-built navigation list (for homeless/search widgets)
	const openModalWithList = useCallback((clickedItem, itemList) => {
		if (!clickedItem) { handleModalData(null); return; }
		const clickedId = clickedItem.id || clickedItem.properties?.id;
		const index = itemList.findIndex(i => (i.id || i.properties?.id) === clickedId);
		modalNavList.current = itemList;
		modalNavIndex.current = index >= 0 ? index : 0;
		modalNavPan.current = true;
		handleModalData(clickedItem);
	}, [handleModalData]);

	// Step the modal nav list by ±1 (used by keyboard arrows on desktop and
	// touch swipes on phone — see ItemModal).
	const stepModalNav = useCallback((dir) => {
		if (modalNavList.current.length === 0) return;
		const len = modalNavList.current.length;
		modalNavIndex.current = (modalNavIndex.current + dir + len) % len;
		const nextItem = modalNavList.current[modalNavIndex.current];
		setMarkedThumbnail(nextItem);
		if (modalNavPan.current) {
			const coords = nextItem?.geometry?.coordinates;
			if (coords && mapRef.current) {
				mapRef.current.easeTo({ center: [coords[0], coords[1]] });
			}
		}
		handleModalData(nextItem);
	}, [handleModalData]);

	// Arrow key handler when modal is open
	useEffect(() => {
		if (!photoModalData) return;
		const handleArrow = (e) => {
			if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
			// Don't hijack arrow keys while the user is typing in a text field —
			// let them move the caret normally.
			const target = e.target;
			if (target && (
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable
			)) return;
			e.preventDefault();
			stepModalNav(e.key === 'ArrowRight' ? 1 : -1);
		};
		document.addEventListener('keydown', handleArrow);
		return () => document.removeEventListener('keydown', handleArrow);
	}, [photoModalData, stepModalNav]);

	// Clear nav list when modal closes (keep pink marker on last viewed item)
	useEffect(() => {
		if (!photoModalData) {
			modalNavList.current = [];
			modalNavIndex.current = -1;
			modalNavPan.current = false;
		}
	}, [photoModalData]);

	// ESC cancels geotagging mode
	useEffect(() => {
		if (!itemToPosition) return;
		const handleEsc = (e) => {
			if (e.key === 'Escape') {
				cancelBeam();
				setItemToPosition(null);
				setItemsToMark([]);
				setPositionPromptOpen(false);
				setSuggestionInputOpen(false);
				if (geotagSourcePanel.current !== null) {
					setRightPanel(geotagSourcePanel.current);
					geotagSourcePanel.current = null;
				}
			}
		};
		document.addEventListener('keydown', handleEsc);
		return () => document.removeEventListener('keydown', handleEsc);
	}, [itemToPosition]);

	return (
		<>
			<Header
				fetchingProgress={fetchingProgress}
				handleSearch={handleSearch}
				rightPanel={rightPanel}
				authData={authData}
				handleAuthData={handleAuthData}
				handleRightPanel={handleRightPanel}
				handleRastersWidgetOpen={handleRastersWidgetOpen}
				rastersWidgetOpen={rastersWidgetOpen}
				handleFilterString={handleFilterString}
				handleSat={toggleMapStyle}
				handleInfoModal={handleInfoModal}
				setEditMode={setEditMode}
				mapRef={mapRef}
				onAddressMarker={setAddressMarker}
				showQuestionableOnly={showQuestionableOnly}
				setShowQuestionableOnly={setShowQuestionableOnly}
				spotlightMode={spotlightMode}
				setSpotlightMode={setSpotlightMode}
				hasActiveRaster={hasActiveRaster}
				rastersHidden={rastersHidden}
				setRastersHidden={setRastersHidden}
				resetRasters={resetRasters}
				lang={lang}
				toggleLang={toggleLang}
				creatorOptions={creatorOptions}
				selectedCreators={selectedCreators}
				setSelectedCreators={setSelectedCreators}
			/>

			<div style={{ position: 'relative', width: '100vw', height: 'calc(100vh - 80px)', marginTop: '80px' }}>
			<Map
				cursor={itemToPosition ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Cline x1='16' y1='3' x2='16' y2='29' stroke='white' stroke-width='6' stroke-linecap='round'/%3E%3Cline x1='3' y1='16' x2='29' y2='16' stroke='white' stroke-width='6' stroke-linecap='round'/%3E%3Cline x1='16' y1='3' x2='16' y2='29' stroke='%23222' stroke-width='3' stroke-linecap='round'/%3E%3Cline x1='3' y1='16' x2='29' y2='16' stroke='%23222' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E") 16 16, crosshair` : "grab"}
				ref={mapRef}
				keyboard={!modalIsOpen}
				onLoad={(e) => {
					const mapGL = mapRef.current.getMap();
					const boundsData = mapGL.getBounds();
					setBounds(boundsData);
					let loadZoom = mapRef.current.getZoom();
					setZoom(loadZoom);
				}}
				onStyleData={onStyleLoad}
				//interactiveLayerIds={!itemToPosition ? ["unclustered-point"] : null}
				//interactiveLayerIds={["unclustered-point",'cluster-count']}
				interactiveLayerIds={['pointLayer']}
				{...viewState}
				onMove={(e) => setViewState(e.viewState)}
				style={{ width: "100%", height: "100%" }}
				mapStyle={mapStyle}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}

				// Events

				onMoveEnd={(e) => {
					setLat(e.viewState.latitude);
					setLng(e.viewState.longitude);
					const mapGL = mapRef.current.getMap();
					const boundsData = mapGL.getBounds();
					setBounds(boundsData);
					setSpiderData([]);
					localStorage.setItem('mapViewState', JSON.stringify({
						longitude: e.viewState.longitude,
						latitude: e.viewState.latitude,
						zoom: e.viewState.zoom
					}));
				}}

				onZoomEnd={(e) => {
					setZoom(e.viewState.zoom);
					setLat(e.viewState.latitude);
					setLng(e.viewState.longitude);
				}}
				
				onMouseDown={(e) => {
					// Close raster sidebar on any map click
					if (rastersWidgetOpen) setRastersWidgetOpen(false);
					try {
						//e.preventDefault();

						let {
							originalEvent: { clientX, clientY },
						} = e;
						let button = e.originalEvent.button;
						const { lngLat } = e;
						const { features } = e;
						if (!button) {
							if (itemToPosition && !beamData) {
								setPositionLngLat(lngLat);

								if (suggesting) {
									setSuggestionInputOpen(true);
								} else {
									//setPositionPromptOpen(true)
									setBeamData([lngLat.lng, lngLat.lat]);
								}
								setItemsToMark([itemToPosition]);
								setInputxy({ x: clientX, y: clientY });
							} else if (!itemToPosition && !beamData  && !editMode) {

								if (features.length > 0) {
									//if (features[0].layer.id === "cluster-count") return
									let itemToModal = features[0];

									// If a thumbnail is selected and its point is among the clicked features,
									// prioritize it to avoid accidentally opening a nearby different item
									if (markedThumbnail) {
										const markedId = markedThumbnail.id || markedThumbnail.properties?.id;
										const markedFeature = features.find(f => f.id === markedId || f.properties?.id === markedId);
										if (markedFeature) {
											itemToModal = markedFeature;
										}
									}

									// Use itemsFromApi (has latest in-place mutations) instead of
									// Mapbox feature (stale stringified properties)
									const apiItem = itemsFromApi.find(i => i.id === itemToModal.id || i.properties?.id === itemToModal.properties?.id);
									if (apiItem) {
										itemToModal = apiItem;
									} else {
										delete itemToModal.properties.coordinates;
										itemToModal.properties["coordinates"] = {
											longitude: itemToModal.geometry.coordinates[0],
											latitude: itemToModal.geometry.coordinates[1],
										};

										if (!Array.isArray(itemToModal.properties.faved_by)) {
											itemToModal.properties.faved_by = JSON.parse(
												itemToModal.properties.faved_by
											);
										}
										if (!Array.isArray(itemToModal.properties.authors)) {
											itemToModal.properties.authors = JSON.parse(
												itemToModal.properties.authors
											);
										}
										if (!Array.isArray(itemToModal.properties.questionable_by)) {
											try {
												itemToModal.properties.questionable_by = JSON.parse(
													itemToModal.properties.questionable_by || '[]'
												);
											} catch(e) {
												itemToModal.properties.questionable_by = [];
											}
										}

									}

									setMarkedThumbnail(itemToModal);
									openModalWithNav(itemToModal);
								} else {
									// Clicked empty map space — clear pink marker
									setMarkedThumbnail(null);
								}
							}
							if (editMode) {

								if (features.length > 0) {
									handleItemsToeEdit([]);
									let itemToEdit = features[0];
									handleItemsToeEdit([itemToEdit]);
									handleMarkedThumbnail(itemToEdit)
								}

							}
						if (itemToPosition && beamData) {
								setBeamData(null);
								setPositionPromptOpen(true);
								setAngle(getAngle(positionLngLat, cursorLngLat));
							}
						}
						// (Removed: legacy right-click "Add location" SuggestionPopup —
						// the button had no working action and confused users.)
					} catch (err) {
						console.log(err);
					}
				}}

				onContextMenu={(e) => {
					// Only open Street View on Ctrl+Right-Click. A bare right-click falls
					// through to the browser's context menu.
					if (!e.originalEvent?.ctrlKey) return;
					e.preventDefault();
					const { lngLat } = e;
					setStreetViewData({ lat: lngLat.lat, lng: lngLat.lng });
				}}


				onMouseEnter={(e) => {
					if (itemToPosition || !e.features) return;
					let cTitles = [
						...new Set(e.features.map((f) => f.properties.title)),
					].join(", ");
					const coordinates = e.features[0].geometry.coordinates.slice();
					const popUpTitle = cTitles;
					mapRef.current.getCanvas().style.cursor = "pointer";

					if (popUpTitle) {
						//setInfoPopupData({ coordinates: coordinates, title: popUpTitle });
					}
				}}

				onMouseLeave={(e) => {
					if (itemToPosition) return;
					mapRef.current.getCanvas().style.cursor = "grab";
					setInfoPopupData(null);
				}}
				
				>
				{/* <SatLayer sat={sat} /> */}

				<RastersWidget
					rastersWidgetOpen={rastersWidgetOpen}
					rasterData={sortedRasterData}
					renderedRasters={renderedRasters}
					setRenderedRasters={setRenderedRasters}
					handleRastersWidgetOpen={handleRastersWidgetOpen}
					rastersHidden={rastersHidden}
					lang={lang}
				/>

				{itemsToMark && positionLngLat && !beamData && (
					<MarkerLayer
						items={itemsToMark}
						coords={[positionLngLat.lng, positionLngLat.lat]}
						handleModalData={handleModalData}
						bounds={bounds}
						selectedMedia={selectedMedia}
						zoom={zoom}
						featureData={filteredItems}
					/>
				)}

				{beamData && <BeamLayer point1={beamData} point2={cursorLngLat} />}
				<PointLayer filteredItems={filteredItems} mapRef={mapRef} markedItemId={markedThumbnail?.id || markedThumbnail?.properties?.id || null} yearBounds={yearBounds}/>
<ExternalWidget
					authData={authData}
					rightPanel={rightPanel}
					handleRightPanel={handleRightPanel}
					mapRef={mapRef.current}
				/>
				<HomelessWidget
					mapRef={mapRef.current}
					authData={authData}
					itemToPosition={itemToPosition}
					handleItemToPosition={handleItemToPosition}
					suggestions={suggestions}
					handleSuggestions={handleSuggestions}
					itemToSuggest={itemToSuggest}
					handleItemToSuggest={handleItemToSuggest}
					rightPanel={rightPanel}
					handleRightPanel={handleRightPanel}

					cancelBeam={cancelBeam}
					handleModalData={handleModalData}
					openModalWithList={openModalWithList}
					handleItemsToMark={handleItemsToMark}
					lastGeotaggedId={lastGeotaggedId}
					lang={lang}
					creatorOptions={creatorOptions}
					widgetCreators={homelessCreators}
					setWidgetCreators={setHomelessCreators}
				/>

				<LocationEditWidget
					mapRef={mapRef.current}
					
					itemToPosition={itemToPosition}
					handleItemToPosition={handleItemToPosition}
					handleDeletionPromptOpen={handleDeletionPromptOpen}
					itemToDelete={itemToDelete}
					handleItemToDelete={handleItemToDelete}

					itemsToEdit={itemsToEdit}
					rightPanel={rightPanel}
					handleRightPanel={handleRightPanel}
					
					cancelBeam={cancelBeam}
					handleModalData={handleModalData}
					handleItemsToMark={handleItemsToMark}
					handleMarkedThumbnail={handleMarkedThumbnail}
				/>

				{positionPromptOpen && itemToPosition && (
					<PositionPrompt
						x={inputxy.x}
						y={inputxy.y}
						angle={angle}
						mapRef={mapRef.current}
						item={itemToPosition}
						authData={authData}
						fetchData={fetchData}
						lngLat={positionLngLat}
						handlePositionPromptOpen={handlePositionPromptOpen}
						handleItemToPosition={handleItemToPosition}
						handleItemsToMark={handleItemsToMark}
						handleItemsToeEdit={handleItemsToeEdit}
						handleRightPanel={handleRightPanel}
						handleMarkedThumbnail={handleMarkedThumbnail}
						setLastGeotaggedId={setLastGeotaggedId}
						geotagSourcePanel={geotagSourcePanel}
					lang={lang}
					/>
				)}

				{deletionPromptOpen && itemToDelete && (
					<DeletionPrompt
						mapRef={mapRef.current}
						itemToDelete={itemToDelete}
						handleItemToDelete={handleItemToDelete}
						handleItemsToeEdit={handleItemsToeEdit}
						authData={authData}
						fetchData={fetchData}
						handleDeletionPromptOpen={handleDeletionPromptOpen}
						handleItemsToMark={handleItemsToMark}
						handleRightPanel={handleRightPanel}
						handleMarkedThumbnail={handleMarkedThumbnail}
					/>
				)}

				{suggestionInputOpen && itemToPosition && (
					<SuggestionInput
						x={inputxy.x}
						y={inputxy.y}
						item={itemToPosition}
						authData={authData}
						lngLat={positionLngLat}
						handleSuggestionUpdate={handleSuggestionUpdate}
						handleSuggestionInputOpen={handleSuggestionInputOpen}
						handleItemToPosition={handleItemToPosition}
						handleRightPanel={handleRightPanel}
						handleItemsToMark={handleItemsToMark}
						handleSuggestions={handleSuggestions}
					/>
				)}

				<ItemModal
					journalModalData={journalModalData}
					handleModalData={handleModalData}
					photoModalData={photoModalData}
					videoModalData={videoModalData}
					authData={authData}
					toggleQuestionable={toggleQuestionable}
					setItemsFromApi={setItemsFromApi}
					handleItemToPosition={handleItemToPosition}
					setLastRemovedLocationId={setLastRemovedLocationId}
					onNavigate={stepModalNav}
					itemComments={itemComments}
					handleComment={handleComment}
					handleComments={setItemComments}
					itemPk={itemPk}
					lang={lang}
				/>

				{markedThumbnail && (() => {
					// Hide the purple marker when the marked item is filtered out by the
					// active creator / year-range / questionable / media filters. The same
					// rules that hide regular dots should hide the selected one.
					const markedId = markedThumbnail.id ?? markedThumbnail.properties?.id;
					const visible = filteredItems.some(it =>
						(it.id ?? it.properties?.id) === markedId
					);
					return visible ? <ThumbMarkerLayer item={markedThumbnail} onMarkerClick={openModalWithNav} passThrough={!!itemToPosition} /> : null;
				})()}
	
				{/* {spiderData.length && (
					<SpiderLayer
						spiderData={spiderData}
						lat={lat}
						zoom={zoom}
						bounds={bounds}
						filterString={filterString}
						itemYears={itemYears}
						handleModalData={handleModalData}
						featureData={filteredItems}
					/>
				)} */}

				{suggestions && (
					<SuggestionsLayer
						handleModalData={handleModalData}
						itemToPosition={itemToPosition}
						bounds={bounds}
						suggestions={suggestions}
					/>
				)}

				<MapLanguageControl position='top-right' />
				<NavigationControl
					position='top-right'
					showZoom={!isPhone}
					style={{ zIndex: 0 }}
				/>
				{isPhone && (
					<GeolocateControl
						position='top-right'
						trackUserLocation={false}
						showUserLocation={true}
					/>
				)}

								{addressMarker && (
					<Marker longitude={addressMarker.lng} latitude={addressMarker.lat} anchor="bottom">
						<div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
							<div style={{
								backgroundColor:'white',
								padding:'2px 8px',
								borderRadius:'4px',
								boxShadow:'0 2px 6px rgba(0,0,0,0.3)',
								fontSize:'12px',
								fontWeight:'bold',
								color:'#333',
								whiteSpace:'nowrap',
								marginBottom:'4px',
								maxWidth:'200px',
								overflow:'hidden',
								textOverflow:'ellipsis'
							}}>
								{addressMarker.name}
							</div>
							<svg width="24" height="36" viewBox="0 0 24 36">
								<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#d32f2f"/>
								<circle cx="12" cy="12" r="5" fill="white"/>
							</svg>
						</div>
					</Marker>
				)}

				{infoPopupData && infoPopupData?.title && (
					<InfoPopup
						title={infoPopupData.title}
						lngLat={infoPopupData.coordinates}
						handlePopupData={handleInfoPopupData}
						selectedMedia={selectedMedia}
					/>
				)}

			</Map>
			{/* Year-range slider.
			    Desktop: vertical, top-left, unified site only.
			    Phone:   horizontal, bottom of screen, on all sites (so per-collection deployments get it too). */}
			{(isPhone || !process.env.REACT_APP_COLLECTION) && (
				<YearRangeSlider
					yearBounds={yearBounds}
					yearRange={yearRange}
					setYearRange={setYearRange}
					lang={lang}
					orientation={isPhone ? 'horizontal' : 'vertical'}
				/>
			)}
			{spotlightMode && hasActiveRaster && (
				<SpotlightOverlay
					viewState={viewState}
					onMove={(e) => setViewState(e.viewState)}
					cursorScreenPos={cursorScreenPos}
					mapStyle={mapStyle}
					mapboxToken={process.env.REACT_APP_MAPBOX_TOKEN}
					filteredItems={filteredItems}
				/>
			)}
			</div>
			{itemToPosition && cursorScreenPos && !positionPromptOpen && (
				<div style={{
					position: 'fixed',
					left: cursorScreenPos.x + 20,
					top: cursorScreenPos.y + 80 + 20,
					background: 'rgba(0,0,0,0.75)',
					color: 'white',
					padding: '4px 8px',
					borderRadius: 4,
					fontSize: 12,
					fontFamily: 'sans-serif',
					pointerEvents: 'none',
					zIndex: 9999,
					whiteSpace: 'nowrap',
				}}>
					{beamData ? 'Set view direction' : 'Pin camera location'}
				</div>
			)}
			{streetViewData && (
				<StreetViewPanel
					lat={streetViewData.lat}
					lng={streetViewData.lng}
					onClose={() => setStreetViewData(null)}
				/>
			)}
			{!isPhone && (
				<ThumbPanel
					handleMarkedThumbnail={handleMarkedThumbnail}
					markedThumbnail={markedThumbnail}
					thumbDrawerOpen={thumbDrawerOpen}
					handleThumbDrawerOpen={handleThumbDrawerOpen}
					mapRef={mapRef.current}
					selectedMedia={selectedMedia}
					handleModalData={handleModalData}
					openModalWithList={openModalWithList}
					modalOpen={!!photoModalData}
					items={thumbPanelItems}
					mapCenter={[lng, lat]}
				/>
			)}
			<UploadModal
				modalOpen={uploadModalOpen}
				handleUploadModal={handleUploadModal}
			/>
			<InfoModal
				modalOpen={infoModalOpen}
				handleInfoModal={handleInfoModal}
			/>
			<SearchWidget
			authData={authData}
				rightPanel={rightPanel}
				handleRightPanel={handleRightPanel}
				handleModalData={handleModalData}
				openModalWithList={openModalWithList}
				mapRef={mapRef.current}
				handleMarkedThumbnail={handleMarkedThumbnail}
				handleItemToPosition={handleItemToPosition}
				cancelBeam={cancelBeam}
				handleItemsToMark={handleItemsToMark}
				lastGeotaggedId={lastGeotaggedId}
				lastRemovedLocationId={lastRemovedLocationId}
				itemToPosition={itemToPosition}
				items={searchItems}
				geotagSourcePanel={geotagSourcePanel}
				lang={lang}
				creatorOptions={creatorOptions}
				widgetCreators={searchCreators}
				setWidgetCreators={setSearchCreators}
			/>
		</>
	);
}
